import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Settings2, Plug, CheckCircle2, XCircle, Loader2, Eye, EyeOff, Trash2,
  RefreshCw, Globe, AlertTriangle, Wifi
} from 'lucide-react';
import { toast } from 'sonner';
import { listATSSettings, saveATSSettings, testATSConnection, deleteATSSettings } from '../services/api';
import { AutoImportPanel } from '../components/ats/AutoImportPanel';

const REGIONS = [
  { key: 'NA', label: 'North America', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { key: 'UK_EMEA', label: 'UK & EMEA', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  { key: 'APAC', label: 'APAC', color: 'text-violet-500', bg: 'bg-violet-500/10' },
];

const RegionATSCard = ({ region, config, onSaved }) => {
  const [editing, setEditing] = useState(!config);
  const [email, setEmail] = useState(config?.email || '');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState(config?.api_url || '');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const status = config?.status;
  const isActive = status === 'active';
  const isError = status === 'error';

  const handleSave = async () => {
    if (!email || !apiUrl || (!config && !apiKey)) { toast.error('Fill all required fields'); return; }
    if (!config && !password) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      await saveATSSettings({
        region: region.key,
        provider: 'ceipal',
        email,
        password,
        api_key: apiKey,
        api_url: apiUrl,
      });
      toast.success(`${region.label} ATS config saved`);
      setEditing(false);
      setPassword('');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testATSConnection(region.key);
      if (result.status === 'active') toast.success(result.message);
      else toast.error(result.message);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ATS config for ${region.label}?`)) return;
    setDeleting(true);
    try {
      await deleteATSSettings(region.key);
      toast.success('Config removed');
      onSaved();
    } catch (err) {
      toast.error('Failed to delete');
    } finally { setDeleting(false); }
  };

  return (
    <Card className={`${isActive ? 'border-green-500/30' : isError ? 'border-red-500/30' : ''}`} data-testid={`ats-card-${region.key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${region.bg}`}>
              <Globe className={`h-4 w-4 ${region.color}`} />
            </div>
            <div>
              <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>{region.label}</CardTitle>
              <p className="text-xs text-muted-foreground">Ceipal ATS Integration</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config ? (
              <>
                <Badge className={`text-[10px] gap-1 ${isActive ? 'bg-green-500/10 text-green-600' : isError ? 'bg-red-500/10 text-red-600' : 'bg-slate-500/10 text-slate-500'}`}>
                  {isActive ? <CheckCircle2 className="h-3 w-3" /> : isError ? <XCircle className="h-3 w-3" /> : <Wifi className="h-3 w-3" />}
                  {isActive ? 'Connected' : isError ? 'Error' : 'Untested'}
                </Badge>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleTest} disabled={testing} data-testid={`test-btn-${region.key}`}>
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Test
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="text-[10px]">Not Configured</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Status message */}
        {config?.last_test_result && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/5 border border-green-500/10">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
            <span className="text-xs text-green-600">{config.last_test_result}</span>
          </div>
        )}
        {config?.last_error && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/5 border border-red-500/10">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
            <span className="text-xs text-red-600">{config.last_error}</span>
          </div>
        )}

        {(editing || !config) ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@company.com" className="h-8 text-sm" data-testid={`ats-email-${region.key}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Password {config && <span className="text-muted-foreground">(blank = keep)</span>}</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="h-8 text-sm pr-8" data-testid={`ats-password-${region.key}`} />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">API Key {config && <span className="text-muted-foreground">(blank = keep {config.api_key})</span>}</Label>
              <Input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={config ? 'Enter a new key to replace it' : 'API key from ATS admin'} className="h-8 text-sm font-mono" data-testid={`ats-apikey-${region.key}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Jobs API URL</Label>
              <Input value={apiUrl} onChange={e => setApiUrl(e.target.value)} placeholder="https://api.ceipal.com/v2/getCustomJobPostingDetails/..." className="h-8 text-sm font-mono" data-testid={`ats-url-${region.key}`} />
            </div>
            <div className="flex gap-2">
              {config && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditing(false)}>Cancel</Button>
              )}
              <Button size="sm" className="text-xs gap-1 flex-1" onClick={handleSave} disabled={saving} data-testid={`ats-save-${region.key}`}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                {config ? 'Update Config' : 'Save & Connect'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{config.email}</span></div>
              <div><span className="text-muted-foreground">Password:</span> <span className="font-medium">••••••••</span></div>
            </div>
            <div className="text-xs"><span className="text-muted-foreground">API URL:</span> <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{config.api_url?.slice(0, 60)}...</code></div>
            {config.last_test && (
              <p className="text-[10px] text-muted-foreground">Last tested: {new Date(config.last_test).toLocaleString()}</p>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setEditing(true); setEmail(config.email); setApiKey(''); setApiUrl(config.api_url); }}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" className="text-xs h-7 text-red-500" onClick={handleDelete} disabled={deleting}>
                <Trash2 className="h-3 w-3 mr-1" /> Remove
              </Button>
            </div>
            <AutoImportPanel regionKey={region.key} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export default function APISettingsPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await listATSSettings();
      setConfigs(data);
    } catch (err) {
      console.error('Failed to load ATS settings:', err);
    } finally { setLoading(false); }
  };

  const getConfigForRegion = (regionKey) => configs.find(c => c.region === regionKey);

  return (
    <div className="space-y-6" data-testid="api-settings-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>API Settings</h1>
        <p className="text-sm text-muted-foreground">Configure ATS integrations for each region</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {REGIONS.map(region => (
            <RegionATSCard
              key={region.key}
              region={region}
              config={getConfigForRegion(region.key)}
              onSaved={loadConfigs}
            />
          ))}
        </div>
      )}
    </div>
  );
}
